import { getAuthToken } from '../Utils/authToken.js';

/**
 * First-party page-view beacon for Quazbit's own analytics (platform admin → Analytics).
 * Posts to the administration server (`/api/analytics/track`). The merchant's token is
 * sent so the server can attribute usage to an admin/organization; it is verified there
 * against /api/admin/profile and never stored. Ported from the elvix storefront beacon.
 */

const SITE = 'dashboard';
const VISITOR_KEY = 'quazbit_platform_analytics_vid';
const SESSION_KEY = 'quazbit_platform_analytics_session';
// Matches the server's idle window so a stale id is not sent only to be replaced.
const SESSION_IDLE_MS = 30 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 8000;
const SUPPRESS_RATE_LIMITED_MS = 10 * 60 * 1000;
const SUPPRESS_BAD_REQUEST_MS = 30 * 60 * 1000;
const SUPPRESS_TRANSIENT_MS = 60 * 1000;
const MAX_CONSECUTIVE_FAILURES = 5;
const MAX_TITLE_LENGTH = 120;

const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;
const VISITOR_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];
const BOT_PATTERN =
    /bot[/;)-]|bot$|crawl|spider|slurp|headless|lighthouse|pagespeed|gtmetrix|phantom|puppeteer|playwright/i;

const BASE_URL = String(
    import.meta.env.VITE_PLATFORM_ANALYTICS_URL || (import.meta.env.DEV ? 'http://localhost:3001' : ''),
).replace(/\/+$/, '');

export const ANALYTICS_ENABLED =
    Boolean(BASE_URL) &&
    String(
        import.meta.env.VITE_ENABLE_PLATFORM_ANALYTICS || (import.meta.env.PROD ? 'true' : 'false'),
    ).toLowerCase() === 'true';

export const IS_BOT =
    typeof navigator !== 'undefined' &&
    (BOT_PATTERN.test(String(navigator.userAgent || '')) || navigator.webdriver === true);

/* ---------- storage (sessionStorage only until a consent banner ships) ---------- */

function readItem(key) {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeItem(key, value) {
    try {
        window.sessionStorage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

function newVisitorId() {
    const webCrypto = typeof window !== 'undefined' ? window.crypto : null;
    if (webCrypto?.randomUUID) return webCrypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

let visitorId = null;

function getVisitorId() {
    if (visitorId) return visitorId;
    const stored = readItem(VISITOR_KEY);
    if (stored && VISITOR_ID_PATTERN.test(stored)) {
        visitorId = stored;
        return visitorId;
    }
    const fresh = newVisitorId();
    // Storage refused: omit the id and let the server derive one from ip + user agent,
    // rather than minting a new "visitor" on every page.
    if (!writeItem(VISITOR_KEY, fresh)) return null;
    visitorId = fresh;
    return visitorId;
}

function readSessionId() {
    try {
        const record = JSON.parse(readItem(SESSION_KEY) || 'null');
        const fresh = record && Date.now() - Number(record.lastSeenAt) <= SESSION_IDLE_MS;
        return fresh && OBJECT_ID_PATTERN.test(String(record.id)) ? String(record.id) : null;
    } catch {
        return null;
    }
}

function writeSessionId(id) {
    if (!OBJECT_ID_PATTERN.test(String(id || ''))) return;
    writeItem(SESSION_KEY, JSON.stringify({ id: String(id), lastSeenAt: Date.now() }));
}

/* ---------- entry attribution, captured once per document ---------- */

function readEntryReferrer() {
    if (typeof document === 'undefined') return '';
    const raw = String(document.referrer || '').trim();
    if (!raw) return '';
    try {
        // Same-site referrers would otherwise count as a referral from ourselves.
        if (new URL(raw).hostname === window.location.hostname) return '';
    } catch {
        return '';
    }
    return raw.slice(0, 2048);
}

function readEntryUtm() {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search || '');
    const utm = {};
    UTM_KEYS.forEach((key) => {
        const value = String(params.get(`utm_${key}`) || '').trim().slice(0, 256);
        if (value) utm[key] = value;
    });
    return Object.keys(utm).length > 0 ? utm : null;
}

const ENTRY_REFERRER = readEntryReferrer();
const ENTRY_UTM = readEntryUtm();

/* ---------- sending ---------- */

let sendChain = Promise.resolve(null);
let suppressUntil = 0;
let consecutiveFailures = 0;
let disabled = false;

function recordFailure(suppressMs) {
    consecutiveFailures += 1;
    suppressUntil = Date.now() + suppressMs;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) disabled = true;
}

async function doSend(pathname) {
    if (disabled || Date.now() < suppressUntil) return null;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;

    const payload = {
        site: SITE,
        // pathname only — never the query string.
        path: pathname.replace(/\/{2,}/g, '/'),
        title: String(document.title || '').slice(0, MAX_TITLE_LENGTH),
    };
    const vid = getVisitorId();
    const sid = readSessionId();
    if (vid) payload.visitorId = vid;
    if (sid) payload.sessionId = sid;
    // Sent on every beacon: only the session's first hit uses them, but a session can
    // restart mid-visit after 30 idle minutes.
    if (ENTRY_REFERRER) payload.referrer = ENTRY_REFERRER;
    if (ENTRY_UTM) payload.utm = ENTRY_UTM;

    const headers = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        const response = await fetch(`${BASE_URL}/api/analytics/track`, {
            method: 'POST',
            keepalive: true,
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            headers,
            body: JSON.stringify(payload),
            signal: controller.signal,
        });

        if (response.status === 201 || response.status === 204) {
            consecutiveFailures = 0;
            if (response.status === 201) {
                const body = await response.json().catch(() => null);
                if (body?.sessionId) writeSessionId(body.sessionId);
            }
            return true;
        }
        if (response.status === 429) {
            // Jittered so clients behind one NAT do not all resume at the same instant.
            recordFailure(SUPPRESS_RATE_LIMITED_MS * (0.75 + Math.random() * 0.5));
        } else if (response.status === 400) {
            recordFailure(SUPPRESS_BAD_REQUEST_MS);
        } else {
            recordFailure(SUPPRESS_TRANSIENT_MS);
        }
        return null;
    } catch {
        // Never retried: a lost response after a recorded hit would double-count.
        recordFailure(SUPPRESS_TRANSIENT_MS);
        return null;
    } finally {
        clearTimeout(timer);
    }
}

/** Fire-and-forget; serialized so each beacon sees the session id of the previous one. */
export function sendPageView(pathname) {
    sendChain = sendChain.then(() => doSend(pathname)).catch(() => null);
    return sendChain;
}
